import React, { useState } from 'react';
import { TeamLogo } from './TeamLogo';

/**
 * ScoreDisplay Component
 * Displays current match score with team logos and short names
 *
 * @param {Object} score - Current score { home, away }
 * @param {Object} homeTeam - Home team data { id, name, short_name, logo_url }
 * @param {Object} awayTeam - Away team data { id, name, short_name, logo_url }
 * @param {Function} onUpdateScore - Callback to update score manually
 * @param {boolean} scoreboardVisible - Whether OBS scoreboard is visible
 * @param {Function} onToggleScoreboard - Callback to toggle scoreboard
 * @param {number} scoreboardTimeout - Auto-hide timeout in ms (0 = manual)
 * @param {Function} onScoreboardTimeoutChange - Callback to change timeout
 * @param {boolean} formationsVisible - Whether formations are visible
 * @param {Function} onToggleFormations - Callback to toggle formations
 * @param {Object} match - Match data with formations
 * @param {boolean} joined - Whether WebSocket is connected
 */
export const ScoreDisplay = ({
  score,
  homeTeam,
  awayTeam,
  onUpdateScore,
  scoreboardVisible,
  onToggleScoreboard,
  scoreboardTimeout = 30000,
  onScoreboardTimeoutChange,
  formationsVisible = false,
  onToggleFormations,
  logoVisible = false,
  onToggleLogo,
  match,
  joined = false
}) => {
  const [isOverrideOpen, setIsOverrideOpen] = useState(false);
  const [manualScore, setManualScore] = useState({ home: 0, away: 0 });

  // Sync manual score with actual score when changed
  React.useEffect(() => {
    setManualScore({ home: score.home, away: score.away });
  }, [score]);

  const handleUpdateScore = () => {
    onUpdateScore(manualScore.home, manualScore.away);
    setIsOverrideOpen(false);
  };

  // Get team display name (prefer short_name, fallback to name or default)
  const getTeamName = (team, defaultName) => {
    if (!team) return defaultName;
    return team.short_name || team.name || defaultName;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg mb-4">
      {/* Main Score Section */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-t-lg">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold">Current Score</h2>
          <div className="flex flex-wrap items-center gap-2">
            {/* Toggle Button */}
            <button
              onClick={onToggleScoreboard}
              disabled={!joined}
              className={`px-3 py-1.5 rounded-lg font-bold text-xs ${
                scoreboardVisible
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white disabled:bg-gray-300'
              }`}
            >
              {scoreboardVisible ? '✓ Scoreboard' : '📺 Scoreboard'}
            </button>

            {/* Timeout Selector */}
            <select
              value={scoreboardTimeout}
              onChange={(e) => onScoreboardTimeoutChange(parseInt(e.target.value))}
              disabled={!joined}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs disabled:bg-gray-100"
              title="Auto-hide timeout"
            >
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
              <option value={60000}>60s</option>
              <option value={0}>Manual</option>
            </select>

            {/* Show Formations Toggle */}
            {(match?.home_formation || match?.away_formation) && (
              <button
                onClick={onToggleFormations}
                disabled={!joined}
                className={`px-3 py-1.5 rounded-lg font-bold text-xs ${
                  formationsVisible
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-500 hover:bg-gray-600 text-white disabled:bg-gray-300'
                }`}
                title="Show formations in overlay"
              >
                {formationsVisible ? '✓ Formations' : '📋 Formations'}
              </button>
            )}

            {/* Broadcaster Logo Toggle */}
            <button
              onClick={onToggleLogo}
              disabled={!joined}
              className={`px-3 py-1.5 rounded-lg font-bold text-xs ${
                logoVisible
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'bg-gray-500 hover:bg-gray-600 text-white disabled:bg-gray-300'
              }`}
              title="Show broadcaster logo in overlay"
            >
              {logoVisible ? '✓ Logo' : '📡 Logo'}
            </button>
          </div>
        </div>

        {scoreboardVisible && (
          <p className="text-xs text-gray-600 mb-3 text-right">
            {scoreboardTimeout === 0 ? 'Manual mode - click button to hide' : `Auto-hide in ${scoreboardTimeout / 1000}s`}
          </p>
        )}

        {/* Score Display with Logos and Names */}
        <div className="flex justify-center items-center gap-6 mb-2">
          {/* HOME TEAM */}
          <div className="flex items-center gap-3">
            {/* Logo (fixed dark background for operator view) */}
            <TeamLogo team={homeTeam} size="w-16 h-16" logoStyle={null} />
            
            {/* Team Name + Score */}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1 font-semibold">
                {getTeamName(homeTeam, 'HOME')}
              </div>
              <div className="text-5xl font-bold text-blue-600">
                {score.home}
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="text-4xl text-gray-400 font-bold">-</div>

          {/* AWAY TEAM */}
          <div className="flex items-center gap-3">
            {/* Team Name + Score */}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1 font-semibold">
                {getTeamName(awayTeam, 'AWAY')}
              </div>
              <div className="text-5xl font-bold text-green-600">
                {score.away}
              </div>
            </div>
            
            {/* Logo (fixed dark background for operator view) */}
            <TeamLogo team={awayTeam} size="w-16 h-16" logoStyle={null} />
          </div>
        </div>
      </div>

      {/* Manual Score Override (Collapsible) */}
      <details 
        open={isOverrideOpen}
        onToggle={(e) => setIsOverrideOpen(e.target.open)}
        className="border-t"
      >
        <summary className="p-4 font-semibold text-gray-700 hover:bg-gray-50 rounded-b-lg cursor-pointer flex items-center gap-2">
          <span>📢</span>
          <span>Manual Score Override</span>
          <span className="ml-auto text-gray-400">▼</span>
        </summary>
        <div className="p-4 border-t">
          <div className="flex gap-4 items-center">
            <input 
              type="number" 
              value={manualScore.home}
              onChange={(e) => setManualScore({ ...manualScore, home: parseInt(e.target.value) || 0 })}
              className="w-20 px-3 py-2 border rounded" 
              placeholder="Home"
              min="0"
            />
            <span className="text-2xl">-</span>
            <input 
              type="number" 
              value={manualScore.away}
              onChange={(e) => setManualScore({ ...manualScore, away: parseInt(e.target.value) || 0 })}
              className="w-20 px-3 py-2 border rounded" 
              placeholder="Away"
              min="0"
            />
            <button 
              onClick={handleUpdateScore}
              disabled={!joined}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
            >
              Update Score
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            ⚠️ Manual override will update the score directly
          </p>
        </div>
      </details>
    </div>
  );
};
